/**
 * 어시스트 채팅 패널(DEF-071 1단계, 시안 A).
 *
 * 버튼 한 번 = 고정 동작이던 코치·요약을 **대화**로 바꾼다. "아까 HS코드
 * 얘기 어디서 나왔지?" 에 답할 수 있어야 하는 것이 요구사항이었다.
 *
 * 규율 세 가지:
 *
 * 1. **근거를 보여 준다.** 답 아래 `refs` 를 그리고 누르면 그 `utt` 로
 *    점프한다. 서버가 먼저 찾고 모델은 답만 쓰므로(§D), 근거 없는 답은
 *    애초에 오지 않는다 — `found=false` 는 모델을 **부르지 않은** 결과다.
 * 2. **열화를 말한다.** 긴호흡이 꺼진 방은 문단이 영원히 0건이다. 그 방은
 *    `context_source: 'captions_only'` 를 싣고 화면이 배너를 띄운다.
 *    기능이 죽는 것이 아니라 **열화된다고 말한다**(§1 조용한 실패 금지).
 * 3. **거절을 말한다.** 429/503 은 문장으로 되돌려 준다. 아무 일도 안
 *    일어난 것처럼 두면 호스트는 답이 오는 줄 알고 기다린다.
 *
 * 자동 코치 카드는 **여기 없다**(2026-09-19 사용자 결정: "코치 기능을 빼면
 * 되. 채팅이 코치 역할을 하는거니까"). `coach-panel.tsx` 는 되돌릴 여지로
 * 남겨 두고 호출만 끊었다.
 */

import { useState } from 'react'
import { Button } from '@cupia/ui/button'
import { Alert, AlertDescription } from '@cupia/ui/alert'
import { Empty } from '@cupia/ui/empty'
import { Info, Send } from 'lucide-react'
import type { AssistItem } from '../lib/reducer'
import type { AssistRef } from '../lib/events'

/**
 * 빠른 버튼 — 자동 코치가 사라진 자리를 메운다. 자동으로 뜨지 않을 뿐,
 * **물으면** 같은 내용을 답한다(`ASSIST_SYSTEM` 규칙 4).
 */
const QUICK_ASKS = ['지금까지 요약', '미답 정리', '결정사항'] as const

function clock(ts: number): string {
  return new Date(ts).toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export interface AssistPanelProps {
  items: AssistItem[]
  /** 질의 전송 — 거절 사유 문장을 던지면 여기서 잡아 화면에 싣는다. */
  onAsk: (q: string) => Promise<void>
  /** 근거를 눌렀을 때 그 발화로 점프 */
  onJump?: (utt: string) => void
  /** 전송 중 — 중복 발사를 막는다(서버도 방당 동시 1건이다) */
  pending: boolean
}

export function AssistPanel({
  items,
  onAsk,
  onJump,
  pending,
}: AssistPanelProps): React.ReactElement {
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)

  // 열화 배너는 **가장 최근 응답**을 근거로 띄운다. 방의 긴호흡 설정은
  // 회의 중에 바뀌지 않지만, 판정의 출처를 서버 응답 하나로 두면 화면이
  // 자기 추측으로 배너를 그리는 일이 없다.
  const last = items[items.length - 1]
  const degraded = last?.data.context_source === 'captions_only'

  async function send(q: string): Promise<void> {
    const text = q.trim()
    if (!text || pending) return
    setError(null)
    setDraft('')
    try {
      await onAsk(text)
    } catch (err) {
      setError(err instanceof Error ? err.message : '요청이 거절되었습니다.')
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3" data-testid="assist-panel">
      {degraded && (
        <Alert variant="warning" data-testid="assist-degraded">
          <Info className="h-4 w-4" />
          <AlertDescription>
            이 방은 긴호흡 기록이 꺼져 있어 실시간 자막만 보고 답합니다.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        {items.length === 0 && (
          <Empty
            title="회의 내용을 물어보세요"
            description="아래 입력창이나 빠른 버튼으로 시작합니다."
          />
        )}

        {items.map((item) => (
          <div key={item.id} className="flex flex-col gap-2">
            {/* 질문 — 서버가 에코한 `q` 를 쓴다. 로컬 낙관적 상태를 두지
                않는 이유: 다른 탭·재접속도 같은 대화를 봐야 한다. */}
            <div
              className="self-end max-w-[85%] rounded-xl rounded-ee-sm bg-[var(--color-primary)] px-3 py-2 text-(length:--text-sm-size) text-[var(--color-primary-foreground)]"
              data-testid="assist-q"
            >
              {item.data.q}
            </div>

            <div
              className="self-start max-w-[92%] rounded-xl rounded-es-sm border border-[var(--color-border)] bg-[var(--color-muted)] px-3 py-2"
              data-testid="assist-a"
            >
              <div className="whitespace-pre-wrap text-(length:--text-sm-size)">
                {item.data.body}
              </div>

              {item.data.truncated && (
                <div className="mt-1 text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
                  기록이 길어 질문과 관련된 대목만 보고 답했습니다.
                </div>
              )}

              {item.data.refs.length > 0 && (
                <div className="mt-2 border-t border-dashed border-[var(--color-border)] pt-1.5">
                  {item.data.refs.map((ref, i) => (
                    <RefRow key={`${item.id}-${i}`} ref_={ref} onJump={onJump} />
                  ))}
                </div>
              )}

              <div className="mt-1 text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
                {clock(item.ts)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <Alert variant="destructive" data-testid="assist-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap gap-1.5">
        {QUICK_ASKS.map((q) => (
          <Button
            key={q}
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={() => void send(q)}
            data-testid={`assist-quick-${q}`}
          >
            {q}
          </Button>
        ))}
      </div>

      <form
        className="flex gap-1.5"
        onSubmit={(e) => {
          e.preventDefault()
          void send(draft)
        }}
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={500}
          placeholder="회의 내용을 물어보세요"
          aria-label="어시스트 질문"
          data-testid="assist-input"
          className="min-w-0 flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-(length:--text-sm-size)"
        />
        <Button
          type="submit"
          size="sm"
          disabled={pending || draft.trim().length === 0}
          aria-label="보내기"
          data-testid="assist-send"
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  )
}

function RefRow({
  ref_,
  onJump,
}: {
  ref_: AssistRef
  onJump?: (utt: string) => void
}): React.ReactElement {
  const jumpable = Boolean(ref_.utt && onJump)
  return (
    <button
      type="button"
      disabled={!jumpable}
      onClick={() => ref_.utt && onJump?.(ref_.utt)}
      data-testid="assist-ref"
      className="block w-full text-start text-(length:--text-xs-size) text-[var(--color-muted-foreground)] disabled:cursor-default"
    >
      <span className="font-medium tabular-nums text-[var(--color-primary)]">
        {clock(ref_.ts)}
      </span>{' '}
      · {ref_.excerpt}
      {ref_.uncertain && (
        <span className="ms-1 text-[var(--color-warning,var(--color-muted-foreground))]">
          불확실
        </span>
      )}
    </button>
  )
}
